import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useVariableSubscription } from '../../../utils';
import { getHasPermission } from '../../../bundles/auth';
import config from '../../../config';
import {
  getSources,
  getSourceTypes,
  requestSources,
  getSourcesLoaded,
  getLinkedSources,
} from '../../../bundles/sources';
import {
  removeSubscriptions as removeVariableSubscriptions,
  getLastVariableData,
} from '../../../bundles/variables';
import { requestModels, getAllModels, getModelsLoaded } from '../../../bundles/models';
import {
  getComponentsLoaded,
  getComponents,
  getStaticComponents,
  getComponentTree,
  getFilteredActiveComponentId,
  getCreatedComponent,
  deleteComponent,
  deleteNonSiteComponents,
  updateComponent,
  createComponent,
  setPageComponents,
  getPageComponents,
} from '../../../bundles/components';
import {
  getActiveModelId,
  getViewerReady,
  setActiveComponentId,
  setActiveModelId,
  setZoomComponentId,
  getPageType,
} from '../../../bundles/application';
import { getAllIntegrations } from '../../../bundles/integrations';
import { getStateSets } from '../../../bundles/statesets';

import prepData from '../../SiteAdminView/ComponentView/utils';

const ComponentPanelContext = createContext();

// Create stable default values outside of the component to prevent recreation
const DEFAULT_LIMIT_COMPONENTS = [];
const DEFAULT_EXCLUDE_COMPONENTS = [];
const DEFAULT_COMPONENT_SCOPE = {};

export const ComponentPanelProvider = ({
  children,
  site,
  panelId,
  pageIndex,
  displayStatus,
  showVirtuals,
  flatList,
  withToolbar,
  autoLoad,
  startingDepth = 1,
  endingDepth = 0,
  expanded = true,
  limitComponents = DEFAULT_LIMIT_COMPONENTS,
  excludeComponents = DEFAULT_EXCLUDE_COMPONENTS,
  componentScope = DEFAULT_COMPONENT_SCOPE,
  showModels = !config?.onPremise,
  showDeleteTreeButton = false,
}) => {
  const dispatch = useDispatch();

  // Extract activeComponentTypeFilter from componentScope
  const { activeComponentTypeFilter } = componentScope;

  // Redux selectors
  const pageComponents = useSelector(getPageComponents);
  const activeComponentId = useSelector(getFilteredActiveComponentId);
  const modelsHashmap = useSelector(getAllModels);
  const modelsLoaded = useSelector(getModelsLoaded);
  const componentsLoaded = useSelector(getComponentsLoaded);
  const components = useSelector(getComponents);
  const staticComponents = useSelector(getStaticComponents);
  const tree = useSelector(getComponentTree);
  const activeModelId = useSelector(getActiveModelId);
  const sources = useSelector(getSources);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const sourceTypes = useSelector(getSourceTypes);
  const createdComponent = useSelector(getCreatedComponent);
  const viewerReady = useSelector(getViewerReady);
  const linkedSources = useSelector(getLinkedSources);
  const integrations = useSelector(getAllIntegrations);
  const lastVariableData = useSelector(getLastVariableData);
  const stateSets = useSelector(getStateSets);
  const pageType = useSelector(getPageType);

  // Local state
  const [pollingVariables, setPollingVariables] = useState([]);
  const [updating, setUpdating] = useState({ show: false, componentId: null });
  const [creating, setCreating] = useState({ show: false });
  const [deleting, setDeleting] = useState({
    show: false,
    componentId: null,
    confirmed: false,
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [hasRootModel, setHasRootModel] = useState(true);

  // Permissions
  const canEdit = useSelector((state) =>
    getHasPermission(state, 'components/Write', { org: site.org, site: site.id })
  );
  const hasDelete = useSelector((state) =>
    getHasPermission(state, 'components/Delete', { org: site.org, site: site.id })
  );
  const canDelete = hasDelete && !site.properties.some((p) => p.name === 'integration');

  // Hook for variable subscription
  useVariableSubscription(panelId, pollingVariables);

  // Computed values
  const staticPageComps = useMemo(
    () =>
      staticComponents?.length && pageComponents?.length
        ? staticComponents.filter((c) => pageComponents.map((w) => w.id).includes(c.id))
        : [],
    [staticComponents, pageComponents]
  );

  const rootComponent = useMemo(
    () =>
      staticComponents.find((c) => c?.type === 'site' && (!('parent' in c) || c?.parent === null)),
    [staticComponents]
  );

  const shownIds = useMemo(() => pageComponents?.map((c) => c.id) || [], [pageComponents]);

  const activeComponent = useMemo(
    () => pageComponents?.find((c) => c.id === activeComponentId),
    [pageComponents, activeComponentId]
  );

  // Callbacks
  const getSourceConfig = useCallback(
    (sourceId) => {
      if (sources && sourceTypes) {
        const selectedSource = sources.find((source) => source.id === sourceId);
        const selectedSourceType = sourceTypes.find(
          (sourceType) => sourceType.name === selectedSource.type
        );
        if (selectedSourceType && selectedSourceType.schemas.config.properties.objectId) {
          return { objectId: selectedSource.options?.iotDevice?.objectId };
        }
        return null;
      }
      return null;
    },
    [sources, sourceTypes]
  );

  const prepCompFormData = useCallback(
    (data) => {
      const { attributeData, ...componentData } = data;
      if (componentData.source) {
        const sourceConfig = getSourceConfig(componentData.source);
        if (sourceConfig) {
          componentData.sourceConfig = sourceConfig;
        }
      }

      return {
        component: prepData(componentData),
        attributes: attributeData,
      };
    },
    [getSourceConfig]
  );

  const handleUpdateComponent = useCallback(
    (formData) => {
      const preparedData = prepCompFormData(formData);
      dispatch(updateComponent(updating.componentId, site.id, preparedData));
      setUpdating({
        show: false,
        componentId: null,
        component: null,
      });
    },
    [updating, prepCompFormData, site.id]
  );

  const handleCloseUpdating = useCallback(() => {
    setUpdating({
      show: false,
      componentId: null,
      component: null,
    });
  }, []);

  const handleCreateComponent = useCallback(
    (formData) => {
      const preparedData = prepCompFormData(formData);
      dispatch(createComponent(site.id, site.org, preparedData));
      setCreating({ show: false });
    },
    [prepCompFormData, site.id, site.org]
  );

  const handleCloseCreating = useCallback(() => setCreating({ show: false }), []);

  // Action handlers
  const handleComponentClick = useCallback(
    (id) => {
      if (rootComponent && id === rootComponent.id) {
        dispatch(setActiveComponentId(null));
      } else {
        dispatch(setActiveComponentId(id));
      }
      dispatch(setZoomComponentId(null));
    },
    [rootComponent]
  );
  const handleComponentDoubleClick = useCallback((id) => {
    dispatch(setZoomComponentId(id));
  }, []);

  const handleModelClick = useCallback((modelId) => {
    dispatch(setActiveModelId(modelId));
  }, []);

  const handleDeleteComponent = useCallback(() => {
    dispatch(deleteComponent(deleting.componentId, site.id));
    setDeleting({
      show: false,
      componentId: null,
      component: null,
      confirmed: false,
    });
  }, [deleting.componentId, site.id]);

  // Effect to handle deleting component setup
  const setupDeletingComponent = useCallback(() => {
    if (deleting.componentId && staticPageComps.length > 0) {
      const component = staticPageComps.find((c) => c.id === deleting.componentId);
      if (component) {
        setDeleting((previous) => ({
          ...previous,
          show: true,
          component,
        }));
      }
    }
  }, [deleting.componentId, staticPageComps]);

  // Effect to handle confirmed deletion
  const executeDeleteComponent = useCallback(() => {
    if (deleting.confirmed && deleting.component) {
      dispatch(deleteComponent(deleting.componentId, site.id));
      setDeleting({
        show: false,
        componentId: null,
        component: null,
        confirmed: false,
      });
    }
  }, [deleting.confirmed, deleting.component, deleting.componentId, site.id]);

  const handleDeleteNonSiteComponents = useCallback(() => {
    dispatch(deleteNonSiteComponents(site.org, site.id));
    setShowDeleteDialog(false);
  }, [site.org, site.id]);

  // Scrolling control
  const [scrollToActive, setScrollToActive] = useState(true);

  const disableAutoScroll = useCallback(() => {
    setScrollToActive(false);
    // Reset after a longer delay to prevent interference with manual clicks
    setTimeout(() => setScrollToActive(true), 300);
  }, []);

  const enableAutoScroll = useCallback(() => {
    setScrollToActive(true);
  }, []);

  const requestInitialData = useCallback(() => {
    if (pageIndex === 'wizard') {
      if (!config?.onPremise && !modelsLoaded) {
        dispatch(requestModels(site.id));
      }
      if (sourcesLoaded !== site.id) dispatch(requestSources({ siteId: site.id }));
    }
  }, [pageIndex, modelsLoaded, sourcesLoaded, site.id]);

  const updatePageComponents = useCallback(() => {
    if (components && components.length > 0) {
      const virtualsAndDescendants = [];
      if (!showVirtuals) {
        for (const c of components.filter((c) => c.type === 'virtual'))
          virtualsAndDescendants.push(c.id, ...c.descendantIds);
      }

      const filteredComponents = components.filter((c) => {
        let include = true;
        if (activeComponentTypeFilter && activeComponentTypeFilter !== 'any') {
          include = c.type === activeComponentTypeFilter;
        }
        if (include && limitComponents.length > 0) {
          include = limitComponents.includes(c.id);
        }
        if (include && excludeComponents.length > 0) {
          include = !excludeComponents.includes(c.id);
        }
        if (include && !showVirtuals) {
          include = !virtualsAndDescendants.includes(c.id);
        }
        return include;
      });

      // Only dispatch if the filtered components are different from current pageComponents
      const currentIds = pageComponents?.map((c) => c.id).toSorted() || [];
      const newIds = filteredComponents.map((c) => c.id).toSorted();

      if (
        currentIds.length !== newIds.length ||
        !currentIds.every((id, index) => id === newIds[index])
      ) {
        dispatch(setPageComponents(filteredComponents));
      }
    }
  }, [
    components,
    limitComponents,
    excludeComponents,
    showVirtuals,
    activeComponentTypeFilter,
    pageComponents,
  ]);

  const updatePollingVariables = useCallback(() => {
    if (pageComponents?.length) {
      const variablesToPoll = pageComponents.reduce((acc, current) => {
        if (current.variables.length > 0) {
          for (const variable of current.variables) acc.add(variable.id);
        }
        return acc;
      }, new Set());
      const variablesToCancelPollingOn = [];
      setPollingVariables((previousVariables) => {
        variablesToCancelPollingOn.push(
          ...previousVariables.filter((v) => !variablesToPoll.has(v))
        );
        return [...variablesToPoll];
      });
      if (variablesToCancelPollingOn.length > 0) {
        dispatch(removeVariableSubscriptions(panelId, variablesToCancelPollingOn));
      }
    }
  }, [pageComponents, panelId]);

  const updateHasRootModel = useCallback(() => {
    if (config?.onPremise) {
      setHasRootModel(false);
    } else {
      setHasRootModel(() => rootComponent?.models?.length > 0);
    }
  }, [rootComponent]);

  // Context value
  const contextValue = {
    // Redux state
    pageComponents,
    activeComponentId,
    modelsHashmap,
    modelsLoaded,
    componentsLoaded,
    components,
    staticComponents,
    tree,
    activeModelId,
    sources,
    sourcesLoaded,
    sourceTypes,
    createdComponent,
    viewerReady,
    linkedSources,
    integrations,
    lastVarData: lastVariableData,
    stateSets,
    pageType,

    // Local state
    updating,
    creating,
    deleting,
    showDeleteDialog,
    hasRootModel,
    pollingVars: pollingVariables,

    // Computed values
    staticPageComps,
    rootComponent,
    shownIds,
    activeComponent,

    // Permissions
    canEdit,
    canDelete,

    // Props passed down
    site,
    panelId,
    pageIndex,
    displayStatus,
    showVirtuals,
    flatList,
    withToolbar,
    autoLoad,
    startingDepth,
    endingDepth,
    expanded,
    limitComponents,
    excludeComponents,
    activeComponentTypeFilter,
    showModels,
    showDeleteTreeButton,

    // Action handlers
    setUpdating,
    setCreating,
    setDeleting,
    setShowDeleteDialog,
    handleUpdateComponent,
    handleCloseUpdating,
    handleCreateComponent,
    handleCloseCreating,
    handleComponentClick,
    handleComponentDoubleClick,
    handleModelClick,
    handleDeleteComponent,
    handleDeleteNonSiteComponents,

    // Scrolling control
    scrollToActive,
    setScrollToActive,
    disableAutoScroll,
    enableAutoScroll,

    // Utility functions
    requestInitialData,
    updatePageComponents,
    updatePollingVars: updatePollingVariables,
    updateHasRootModel,
    setupDeletingComponent,
    executeDeleteComponent,
  };

  return (
    <ComponentPanelContext.Provider value={contextValue}>{children}</ComponentPanelContext.Provider>
  );
};

export const useComponentPanel = () => {
  const context = useContext(ComponentPanelContext);
  if (!context) {
    throw new Error('useComponentPanel must be used within a ComponentPanelProvider');
  }
  return context;
};
